import { type AccountSummary, LinkState } from '@fruitful/types';
import React from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Regular,
    HolisticAccountLogo,
    LinkIcon,
    Separator,
    Sheet,
    TrashIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import AccountStateIcon from '../../banking/components/icons/AccountStateIcon';

interface AccountErrorSheetProps {
    account: AccountSummary;
    isOpen: boolean;
    onClose: () => void;
    onPressRelink: () => void;
    onPressRecategorize: () => void;
    onPressHide: () => void;
}

const AccountErrorSheet = ({
    account,
    isOpen,
    onClose,
    onPressRelink,
    onPressRecategorize,
    onPressHide,
}: AccountErrorSheetProps) => {
    const { logo, name, institutionName, linkState, needsRecategorization } =
        account;

    let content: React.JSX.Element;

    if (linkState === LinkState.ERROR) {
        content = (
            <RelinkOrHide
                onClose={onClose}
                onPressRelink={onPressRelink}
                onPressHide={onPressHide}
            />
        );
    } else if (needsRecategorization) {
        content = (
            <Recategorize
                onClose={onClose}
                onPressRecategorize={onPressRecategorize}
            />
        );
    } else {
        content = <></>;
    }

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Account error"
            showCloseButton
        >
            <YStack alignItems="center" gap={12}>
                <XStack>
                    <HolisticAccountLogo
                        institutionName={institutionName}
                        logo={logo}
                        size="lg"
                    />
                    <View position="absolute" marginLeft={54}>
                        <AccountStateIcon state="error" size="lg" />
                    </View>
                </XStack>
                <H2Regular textAlign="center">{name}</H2Regular>
                {content}
            </YStack>
        </Sheet>
    );
};

interface RelinkProps {
    onClose: () => void;
    onPressRelink: () => void;
    onPressHide: () => void;
}

const RelinkOrHide = ({ onClose, onPressRelink, onPressHide }: RelinkProps) => {
    return (
        <YStack width="100%">
            <BodyMediumRegular textAlign="center" paddingHorizontal={60}>
                There&apos;s an error with this institution&apos;s link. Please
                try reconnecting.
            </BodyMediumRegular>
            <Separator width="100%" marginVertical={16} />
            <YStack gap={8}>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Relink"
                    onPress={() => {
                        onClose();
                        onPressRelink();
                    }}
                    icon={LinkIcon}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Hide"
                    onPress={() => {
                        onPressHide();
                    }}
                    icon={TrashIcon}
                />
            </YStack>
        </YStack>
    );
};

interface RecategorizeProps {
    onClose: () => void;
    onPressRecategorize: () => void;
}

const Recategorize = ({ onClose, onPressRecategorize }: RecategorizeProps) => {
    return (
        <YStack width="100%">
            <BodyMediumRegular textAlign="center">
                We were unable to determine the account type. Please categorize
                this account to view the balance.
            </BodyMediumRegular>
            <Separator width="100%" marginVertical={16} />
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                text="Categorize"
                onPress={() => {
                    onClose();
                    onPressRecategorize();
                }}
            />
        </YStack>
    );
};

export default AccountErrorSheet;
